'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import {
  buildContext,
  buildCriterionExpression,
  buildFieldSortCriterion,
  buildRelevanceSortCriterion,
  buildSort,
  SortOrder,
} from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { c, Dropdown, DropdownOption } from 'ui-components';
import { useEngine } from '../EngineContext';
import { SortOption, SortProps } from './Sort.types';

const RELEVANCE_SORT: SortOption = {
  value: 'relevance',
  criterion: buildRelevanceSortCriterion(),
};

const POPULARITY_SORT: SortOption = {
  value: 'popularity',
  criterion: buildRelevanceSortCriterion(),
};

export const SEARCH_SORT_CRITERIA: SortOption[] = [
  RELEVANCE_SORT,
  POPULARITY_SORT,
  {
    value: 'rating',
    criterion: buildFieldSortCriterion('ec_rating_sort', SortOrder.Descending),
  },
  {
    value: 'published-date',
    criterion: buildFieldSortCriterion(
      'first_published_at',
      SortOrder.Descending
    ),
  },
  {
    value: 'recently-updated',
    criterion: buildFieldSortCriterion(
      'last_published_at',
      SortOrder.Descending
    ),
  },
  {
    value: 'price-low-to-high',
    criterion: buildFieldSortCriterion('ec_price_filter', SortOrder.Ascending),
  },
  {
    value: 'price-high-to-low',
    criterion: buildFieldSortCriterion('ec_price_filter', SortOrder.Descending),
  },
];

const getCriterionFromValue = (value: string) =>
  SEARCH_SORT_CRITERIA.find((opt) => opt.value === value)?.criterion ??
  RELEVANCE_SORT.criterion;

export const Sort = ({ className }: SortProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const sortByLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.sort-by')?.value ??
    'Sort by';

  const eng = useEngine();
  const sortController = useMemo(
    () =>
      buildSort(eng, {
        initialState: { criterion: RELEVANCE_SORT.criterion },
      }),
    [eng]
  );
  const coveoContext = useMemo(() => buildContext(eng), [eng]);
  const state = useController(sortController);

  const [currentSortValue, setCurrentSortValue] = useState(
    RELEVANCE_SORT.value
  );

  const getValueFromCriterionString = useCallback(
    (criterion: string) =>
      SEARCH_SORT_CRITERIA.find(
        (opt) => buildCriterionExpression(opt.criterion) === criterion
      )?.value ?? RELEVANCE_SORT.value,
    []
  );

  // Sync sort value when URL restores a non-relevancy sort
  useEffect(() => {
    if (state.sortCriteria !== 'relevancy') {
      setCurrentSortValue(getValueFromCriterionString(state.sortCriteria));
      // @ts-ignore — Coveo context accepts boolean despite TS typing
      coveoContext.add('popularity', false);
    }
  }, [state.sortCriteria, getValueFromCriterionString, coveoContext]);

  const options: DropdownOption[] = useMemo(
    () =>
      SEARCH_SORT_CRITERIA.map((opt) => {
        const key = `search.${opt.value}`;
        const label =
          searchLabelsMicrocopy?.find((m) => m.key === key)?.value ??
          opt.value.charAt(0).toUpperCase() + opt.value.slice(1);
        return { label, value: opt.value };
      }),
    [searchLabelsMicrocopy]
  );

  const baseOption =
    options.find((opt) => opt.value === currentSortValue) ?? options[0];

  const selectedOption: DropdownOption = useMemo(
    () => ({
      ...baseOption,
      label: (
        <>
          <span className="text-grey-600">{sortByLabel}:</span>{' '}
          <span className="font-normal text-[#141414] opacity-90">
            {baseOption.label}
          </span>
        </>
      ),
    }),
    [baseOption, sortByLabel]
  );

  const handleSelect = (option: DropdownOption) => {
    const sortValue = option.value;

    if (sortValue === 'popularity') {
      // @ts-ignore — Coveo context accepts boolean despite TS typing
      coveoContext.add('popularity', true);
    } else {
      // @ts-ignore — Coveo context accepts boolean despite TS typing
      coveoContext.add('popularity', false);
    }

    sortController.sortBy(getCriterionFromValue(sortValue));
    setCurrentSortValue(sortValue);
  };

  return (
    <div className={c('min-w-[180px]', className)}>
      <Dropdown
        id="search-sort"
        options={options}
        selectedOption={selectedOption}
        onSelect={handleSelect}
        isDarkTheme={false}
        buttonClassName="bg-transparent"
        ariaLabel={`Sort by ${baseOption.label}`}
      />
    </div>
  );
};
